import React, { useState, useEffect, useCallback } from "react";
import { CheckIcon, XMarkIcon, UsersIcon } from "@heroicons/react/24/outline";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { apiService } from "../services/api";
import toast from "react-hot-toast";

const GroupsSelectionModal = ({
  isOpen,
  onClose,
  session,
  clubId,
  onGroupsUpdated,
}) => {
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAvailableGroups = useCallback(async () => {
    try {
      setLoading(true);
      const groups = await apiService.getGroupsByClub(clubId);
      setAvailableGroups(groups);
    } catch (error) {
      console.error("Error loading available groups:", error);
      toast.error("Failed to load available groups");
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  const loadCurrentSessionGroups = useCallback(async () => {
    if (!session?.id) return;

    try {
      const sessionGroups = await apiService.getSessionGroups(session.id);

      // Pre-select currently assigned groups
      const selectedGroupIds = sessionGroups.map((sg) => sg.groups.id);
      setSelectedGroups(selectedGroupIds);
    } catch (error) {
      console.error("Error loading session groups:", error);
      // Don't show error toast here as this might be expected for new sessions
      setSelectedGroups([]);
    }
  }, [session?.id]);

  useEffect(() => {
    if (isOpen && session && clubId) {
      loadAvailableGroups();
      loadCurrentSessionGroups();
    }
  }, [isOpen, session, clubId, loadAvailableGroups, loadCurrentSessionGroups]);

  const handleGroupToggle = (groupId) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const groupsToSave = selectedGroups.map((groupId) => ({
        id: groupId,
        weight: 1.0,
        is_required: true,
      }));

      await apiService.saveSessionGroups(session.id, { groups: groupsToSave });

      toast.success("Groups updated successfully!");
      onGroupsUpdated && onGroupsUpdated();
      onClose();
    } catch (error) {
      console.error("Error saving session groups:", error);
      toast.error("Failed to update session groups");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedGroups([]);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Manage Groups for ${session?.name}`}
      size="lg"
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Select which groups should be available for evaluation in this
            session.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Available Groups</h4>

            {availableGroups.length === 0 ? (
              <div className="text-center py-8">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No groups available
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Create some groups first before assigning them to sessions.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {availableGroups.map((group) => {
                  const isSelected = selectedGroups.includes(group.id);

                  return (
                    <div
                      key={group.id}
                      className={`
                        relative rounded-lg border p-4 cursor-pointer transition-colors
                        ${
                          isSelected
                            ? "border-slate-300 bg-slate-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }
                      `}
                      onClick={() => handleGroupToggle(group.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: group.color }}
                          />
                          <div>
                            <h5 className="font-medium text-gray-900">
                              {group.name}
                            </h5>
                            {group.description && (
                              <p className="text-sm text-gray-600">
                                {group.description}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {isSelected && (
                            <CheckIcon className="w-5 h-5 text-slate-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selectedGroups.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Selected Groups ({selectedGroups.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {selectedGroups.map((groupId) => {
                const group = availableGroups.find((g) => g.id === groupId);
                return group ? (
                  <Badge
                    key={groupId}
                    variant="blue"
                    className="flex items-center space-x-1"
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: group.color }}
                    />
                    <span>{group.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGroupToggle(groupId);
                      }}
                      className="ml-1 hover:text-red-600"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="min-w-[80px]"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export { GroupsSelectionModal };
